
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error' | 'already_updated'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        console.log("No session ID found in URL");
        setVerificationStatus('error');
        setErrorMessage('No payment session found');
        setIsLoading(false);
        return;
      }

      try {
        console.log("Verifying payment status for session:", sessionId);
        
        // Call the verify-payment-status edge function
        const { data, error } = await supabase.functions.invoke('verify-payment-status', {
          body: { sessionId }
        });

        if (error) {
          console.error("Error verifying payment:", error);
          throw new Error(error.message || 'Payment verification failed');
        }

        console.log("Payment verification response:", data);

        if (data.success) {
          if (data.message === "Application already updated") {
            setVerificationStatus('already_updated');
            toast({
              title: "Payment Already Processed",
              description: "Your payment has already been confirmed and your application is active.",
            });
          } else {
            setVerificationStatus('success');
            toast({
              title: "Payment Verified!",
              description: "Your payment has been confirmed and your application status has been updated.",
            });
          }
        } else {
          throw new Error(data.error || 'Payment verification failed');
        }
      } catch (error) {
        console.error("Payment verification error:", error);
        setVerificationStatus('error');
        setErrorMessage(error.message || 'Failed to verify payment');
        toast({
          title: "Verification Error",
          description: "We couldn't verify your payment automatically. Your payment was successful, but please contact support if your application status doesn't update.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId, toast]);

  const getStatusIcon = () => {
    switch (verificationStatus) {
      case 'success':
      case 'already_updated':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-16 h-16 text-yellow-500" />;
      default:
        return <CheckCircle className="w-16 h-16 text-green-500" />;
    }
  };

  const getStatusTitle = () => {
    switch (verificationStatus) {
      case 'success':
        return "Payment Verified!";
      case 'already_updated':
        return "Payment Already Processed";
      case 'error':
        return "Payment Successful";
      default:
        return "Payment Successful";
    }
  };

  const getStatusDescription = () => {
    switch (verificationStatus) {
      case 'success':
        return "Your payment has been confirmed and your mentorship program is now active. Your mentor will be in touch soon.";
      case 'already_updated':
        return "Your payment was already processed and your mentorship program is active.";
      case 'error':
        return `Your payment was successful, but we encountered an issue updating your application status automatically. ${errorMessage ? `Error: ${errorMessage}` : ''} Please contact support if needed.`;
      default:
        return "Your payment has been processed successfully.";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-8 h-8 border-2 border-emu-blue border-t-transparent rounded-full animate-spin" />
            <p className="text-lg">Verifying your payment...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-grow pt-8 pb-12 flex items-center justify-center">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto text-center">
            <Card className="p-8">
              <div className="space-y-6">
                <div className="flex justify-center">
                  {getStatusIcon()}
                </div>
                
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {getStatusTitle()}
                  </h1>
                  <p className="text-gray-600">
                    {getStatusDescription()}
                  </p>
                </div>

                {sessionId && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">
                      Payment Reference: {sessionId}
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  <Button 
                    onClick={() => navigate('/mentee-dashboard')}
                    className="w-full bg-emu-blue hover:bg-emu-blue/90"
                  >
                    Go to Dashboard
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/messages')}
                    className="w-full"
                  >
                    View Messages
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
